import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Lock, Wallet, ShieldCheck, AlertTriangle, Loader2, CheckCircle2, CreditCard } from "lucide-react";
import { motion } from "framer-motion";
import { euro } from "@/lib/format";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { prepareExternal, openExternal, cancelExternal } from "@/lib/external";

// Unlock / payment confirmation modal.
const UnlockPaymentModal = ({ open, onOpenChange, photo, balance, feePercent = 20, onConfirm, onRecharge }) => {
  const [status, setStatus] = useState("idle"); // idle | processing | success
  const price = photo?.price || 0;
  const fee = Math.round(price * feePercent) / 100;
  const creator = Math.round((price - fee) * 100) / 100;
  const insufficient = balance < price;
  const missing = Math.max(0, Math.round((price - balance) * 100) / 100);

  const handleConfirm = async () => {
    setStatus("processing");
    try {
      await onConfirm(photo);
      setStatus("success");
      setTimeout(() => {
        setStatus("idle");
        onOpenChange(false);
      }, 1000);
    } catch (e) {
      setStatus("idle");
    }
  };

  const payByCard = async () => {
    const reserved = prepareExternal();
    setStatus("processing");
    try {
      const { data } = await api.post(`/photos/${photo.id}/checkout`, {
        origin_url: window.location.origin,
        return_path: window.location.pathname,
      });
      // Redirection vers le paiement Stripe — la photo sera débloquée
      // uniquement après confirmation serveur du paiement.
      if (openExternal(data.checkout_url, reserved) === "tab") {
        toast.info("Le paiement Stripe s'est ouvert dans un nouvel onglet.");
        setStatus("idle");
      }
    } catch (e) {
      cancelExternal(reserved);
      toast.error(apiError(e));
      setStatus("idle");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (status === "processing" ? null : onOpenChange(v))}>
      <DialogContent className="max-w-sm rounded-2xl">
        <DialogHeader>
          <div className="mx-auto h-12 w-12 rounded-2xl bg-primary/10 text-primary grid place-items-center mb-1">
            <Lock className="h-6 w-6" />
          </div>
          <DialogTitle className="text-center font-display">Déverrouiller la photo</DialogTitle>
          <DialogDescription className="text-center">
            Cette photo coûte <strong>{euro(price)}</strong> pour être déverrouillée.
          </DialogDescription>
        </DialogHeader>

        {status === "success" ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="py-6 flex flex-col items-center gap-2 text-[hsl(160_84%_32%)]"
            data-testid="unlock-modal-success"
          >
            <CheckCircle2 className="h-12 w-12" />
            <p className="font-medium">Photo déverrouillée</p>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {/* Wallet balance */}
            <div className="flex items-center justify-between rounded-xl bg-secondary px-3 py-2.5">
              <span className="flex items-center gap-2 text-sm text-muted-foreground">
                <Wallet className="h-4 w-4" /> Votre solde
              </span>
              <span data-testid="unlock-modal-wallet-balance" className="font-semibold">
                {euro(balance)}
              </span>
            </div>

            {/* Fee breakdown */}
            <div data-testid="unlock-modal-fee-breakdown" className="rounded-xl border border-border p-3 space-y-2 text-sm">
              <div className="flex items-center justify-between text-muted-foreground">
                <span>Commission plateforme ({feePercent}%)</span>
                <span>{euro(fee)}</span>
              </div>
              <div className="flex items-center justify-between text-muted-foreground">
                <span>Reversé au créateur ({100 - feePercent}%)</span>
                <span>{euro(creator)}</span>
              </div>
              <Separator />
              <div className="flex items-center justify-between font-semibold">
                <span>Total à payer</span>
                <span>{euro(price)}</span>
              </div>
            </div>

            {insufficient ? (
              <div data-testid="unlock-modal-insufficient-funds" className="rounded-xl bg-[hsla(38,92%,50%,0.12)] p-3 text-sm">
                <div className="flex items-center gap-2 text-[hsl(38_92%_38%)] font-medium">
                  <AlertTriangle className="h-4 w-4" /> Solde insuffisant
                </div>
                <p className="text-muted-foreground mt-1">Il vous manque {euro(missing)} pour débloquer cette photo.</p>
                <Button
                  variant="outline"
                  className="w-full mt-2 rounded-xl"
                  data-testid="unlock-modal-recharge-button"
                  onClick={() => {
                    onOpenChange(false);
                    onRecharge && onRecharge();
                  }}
                >
                  Recharger mon solde
                </Button>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground flex items-center gap-1.5 justify-center">
                <ShieldCheck className="h-3.5 w-3.5" /> Paiement sécurisé • accès immédiat après paiement
              </p>
            )}

            <div className="flex gap-2">
              <Button variant="ghost" className="flex-1 rounded-xl" onClick={() => onOpenChange(false)} disabled={status === "processing"}>
                Annuler
              </Button>
              <Button
                className="flex-1 rounded-xl neon-glow-blue-sm"
                data-testid="unlock-modal-confirm-button"
                disabled={insufficient || status === "processing"}
                onClick={handleConfirm}
              >
                {status === "processing" ? (
                  <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Paiement…</>
                ) : (
                  <>Payer avec le solde — {euro(price)}</>
                )}
              </Button>
            </div>

            <div className="relative py-1">
              <Separator />
              <span className="absolute left-1/2 -translate-x-1/2 -top-0.5 bg-background px-2 text-[10px] uppercase tracking-wide text-muted-foreground">ou</span>
            </div>

            <Button
              variant="outline"
              className="w-full rounded-xl"
              data-testid="unlock-modal-card-button"
              disabled={status === "processing"}
              onClick={payByCard}
            >
              {status === "processing" ? (
                <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Redirection…</>
              ) : (
                <><CreditCard className="h-4 w-4 mr-1.5" /> Payer par carte — {euro(price)}</>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UnlockPaymentModal;
